'use client'

import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react'
import { apiCheckNamekey } from '@/app/_libs/apiClient'
import { testNamekey } from '@/app/_libs/utilsMsg'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchDeskNavigation, type DeskViewMode } from '@/msgdeskRuntime/deskNavigation'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { AuthEntry } from '@/deskUiSkeletons/Shared/AuthEntry'
import { DeskStateBadge } from '@/deskUiSkeletons/Shared/DeskStateBadge'
import { ParentsMenu } from '@/deskUiSkeletons/Menu/ParentsMenu'
import { useDeskMenu } from '@/deskUiSkeletons/Menu/menuModel'

const labels: Record<DeskViewMode, string> = {
  msg: 'Article',
  answers: 'Answers',
  submsges: 'Messages',
  attachments: 'Files',
  favs: 'Saved',
  drafts: 'Drafts',
  trash: 'Trash',
}

function WikiSearch() {
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const clean = query.replace(/\s+/g, ' ').trim()
    if (!clean || !testNamekey(clean) || busy) return

    setBusy(true)
    try {
      const codeKey = await apiCheckNamekey(clean, '', window.location.origin)
      if (codeKey) window.location.assign(`/${codeKey}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="flex h-8 w-full max-w-[475px]" role="search" onSubmit={submit}>
      <label className="sr-only" htmlFor="md-wiki-search">Search MsgDesk</label>
      <div className="relative min-w-0 flex-1">
        <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-[15px] text-[#54595d]" aria-hidden="true">⌕</span>
        <input
          id="md-wiki-search"
          type="search"
          value={query}
          onChange={event => setQuery(event.currentTarget.value)}
          className="h-8 w-full border border-[var(--wiki-border)] bg-[var(--wiki-bg)] py-1 pl-8 pr-3 text-[14px] text-[var(--wiki-text)] outline-none placeholder:text-[var(--wiki-muted)] focus:border-[#36c] focus:shadow-[inset_0_0_0_1px_#36c]"
          placeholder="Search MsgDesk"
          autoComplete="off"
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        className="h-8 border border-l-0 border-[var(--wiki-border)] bg-[var(--wiki-subtle)] px-4 text-[14px] font-semibold text-[var(--wiki-text)] hover:brightness-105 disabled:cursor-default disabled:text-[var(--wiki-muted)]"
      >
        Search
      </button>
    </form>
  )
}

export function WikiMenu({
  isEditorOpen,
  editorMode,
  settingsOpen,
  onToggleSettings,
}: {
  isEditorOpen: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
}) {
  const menu = useDeskMenu()
  const locked = !menu.access.showUnlocked
  const [mainMenuOpen, setMainMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const saveLabel = editorMode === 'new_answer'
    ? 'Save page'
    : editorMode === 'new_submsg' || editorMode === 'new_msg'
      ? 'Save message'
      : 'Save changes'

  useEffect(() => {
    if (!mainMenuOpen) return
    const close = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMainMenuOpen(false)
    }
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMainMenuOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', closeWithEscape)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', closeWithEscape)
    }
  }, [mainMenuOpen])

  function navigate(event: MouseEvent<HTMLButtonElement>, mode: DeskViewMode) {
    setMainMenuOpen(false)
    dispatchDeskNavigation({ mode, target: event.currentTarget })
  }

  return (
    <header
      {...legacyContractDataAttrs('sidebar.root')}
      className={`md-wiki-header bg-[var(--wiki-bg)] text-[var(--wiki-text)]${locked ? ' hidden' : ''}`}
      style={locked ? { display: 'none' } : undefined}
    >
      <div className="mx-auto flex min-h-[72px] w-full max-w-[1600px] items-center gap-5 px-4 py-2 sm:px-8">
        <div ref={menuRef} className="relative flex flex-none items-center gap-3">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center text-[22px] leading-none hover:bg-[#eaecf0]"
            aria-label="Main menu"
            aria-expanded={mainMenuOpen}
            onClick={() => setMainMenuOpen(open => !open)}
          >
            ≡
          </button>
          <a href="/" className="hidden min-w-[170px] text-[var(--wiki-text)] no-underline sm:block">
            <span className="block font-serif text-[24px] leading-6 tracking-[0.02em]">MsgDesk</span>
            <span className="block pt-0.5 text-[10px] tracking-[0.04em] text-[var(--wiki-muted)]">the message encyclopedia</span>
          </a>

          {mainMenuOpen ? (
            <div className="absolute left-0 top-[44px] z-[100020] w-[260px] border border-[var(--wiki-border)] bg-[var(--wiki-bg)] p-3 text-[14px] shadow-[0_2px_8px_rgba(0,0,0,0.2)]">
              <strong className="mb-2 block border-b border-[#eaecf0] pb-2 text-[14px]">Navigation</strong>
              <div className="flex flex-col items-stretch">
                {menu.tabs
                  .filter(tab => tab.available && (tab.mode === 'msg' || tab.hasItems) && (!tab.requiresEdit || menu.access.editUnlocked) && (tab.mode !== 'favs' || menu.can.favs))
                  .map(tab => (
                    <button
                      key={tab.mode}
                      type="button"
                      className={`px-2 py-1.5 text-left text-[var(--wiki-link)] hover:bg-[var(--wiki-subtle)]${tab.isActive ? ' font-semibold text-[var(--wiki-text)]' : ''}`}
                      aria-current={tab.isActive ? 'page' : undefined}
                      onClick={event => navigate(event, tab.mode)}
                    >
                      {labels[tab.mode]}
                    </button>
                  ))}
              </div>

              <div className="mt-3 border-t border-[#eaecf0] pt-2">
                {menu.can.reply ? (
                  <button type="button" className="block w-full px-2 py-1.5 text-left text-[var(--wiki-link)] hover:bg-[var(--wiki-subtle)]" onClick={() => dispatchEditorAction({ action: 'open-reply' })}>
                    Create page
                  </button>
                ) : null}
                {menu.can.createMessage ? (
                  <button type="button" className="block w-full px-2 py-1.5 text-left text-[var(--wiki-link)] hover:bg-[var(--wiki-subtle)]" onClick={() => dispatchEditorAction({ action: 'open-new-message' })}>
                    Create message
                  </button>
                ) : null}
                {menu.access.editUnlocked ? (
                  <button type="button" className="block w-full px-2 py-1.5 text-left text-[var(--wiki-link)] hover:bg-[var(--wiki-subtle)]" onClick={() => dispatchEditorAction({ action: 'open-edit-message' })}>
                    Edit article
                  </button>
                ) : null}
              </div>

              <ParentsMenu
                excludeMsgType="wiki"
                labels={{ trigger: 'Parents', home: 'MsgDesk.com' }}
                skin={{
                  root: 'mt-2 border-t border-[#eaecf0] pt-2',
                  trigger: 'flex w-full cursor-pointer items-center justify-between px-2 py-1.5 text-left text-[var(--wiki-link)] hover:bg-[var(--wiki-subtle)]',
                  triggerOpen: 'bg-[var(--wiki-subtle)]',
                  count: 'text-[11px] text-[#72777d]',
                  list: 'mt-1 flex min-w-0 flex-col border-l-2 border-[#eaecf0] pl-2',
                  link: 'truncate px-2 py-1 text-[var(--wiki-link)] no-underline hover:underline',
                  homeLink: 'mt-1 border-t border-[#eaecf0] px-2 py-1.5 text-[var(--wiki-muted)] no-underline hover:underline',
                }}
              />
            </div>
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <WikiSearch />
        </div>

        <div className="hidden flex-none items-center gap-3 text-[13px] md:flex">
          <DeskStateBadge skin={{
            root: 'flex items-center whitespace-nowrap text-[12px]',
            possessedOpen: 'text-[#14866d]',
            possessedPin: 'text-[#14866d]',
            locked: 'text-[#72777d]',
            label: 'cursor-pointer border-0 bg-transparent p-0 text-inherit underline decoration-dotted underline-offset-2',
            form: 'ml-1.5 inline-flex items-center gap-1',
            input: 'h-7 w-[105px] border border-[var(--wiki-border)] bg-[var(--wiki-bg)] px-2 text-[12px] outline-none focus:border-[#36c]',
            button: 'h-7 px-2 text-[12px] text-[#36c] hover:bg-[#eaecf0]',
          }} />
          <AuthEntry skin={{
            root: 'flex items-center gap-2',
            signInButton: 'h-8 px-2 font-semibold text-[#36c] hover:bg-[#eaecf0]',
            identity: 'flex items-center gap-2',
            identityLink: 'max-w-[145px] overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-[#36c] hover:underline',
            signOutButton: 'h-8 px-2 text-[#36c] hover:bg-[#eaecf0]',
          }} />
        </div>
      </div>

      {isEditorOpen ? (
        <div className="border-y border-[var(--wiki-border)] bg-[var(--wiki-subtle)]">
          <div className="mx-auto flex min-h-10 w-full max-w-[960px] items-center justify-end gap-2 px-4 py-1">
            <button
              type="button"
              className="bg-[#36c] px-3 py-1.5 text-[14px] font-semibold text-white hover:bg-[#2a4b8d]"
              onClick={event => dispatchEditorAction({ action: 'save', target: event.currentTarget, reason: 'wiki-menu:before-save' })}
            >
              {saveLabel}
            </button>
            <button type="button" className="px-3 py-1.5 text-[14px] text-[#36c] hover:bg-[#eaecf0]" aria-pressed={settingsOpen} onClick={onToggleSettings}>
              {settingsOpen ? 'Content' : 'Configure'}
            </button>
            <button type="button" className="px-3 py-1.5 text-[14px] text-[#b32424] hover:bg-[#fee7e6]" onClick={event => navigate(event, 'msg')}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </header>
  )
}
